/**
 * Painted Idols — add to cart without leaving the page.
 *
 * INTERACTIE.md §6 describes what adding to cart should do: "the button keeps
 * its text and gets a progress line underneath. Then a drawer slides open on
 * the right with the canvas in it."
 *
 * What it did instead: sections/pi-product.liquid posts a plain
 * {% form 'product' %}, so the browser did a native POST and Shopify redirected
 * to /cart. The drawer was rendered on every page by layout/theme.liquid and
 * never opened — Horizon opens it from a cartLinesUpdate event, and nothing on
 * this site fires one.
 *
 * This module hangs off the form from the outside, matching on the class the
 * product section already uses, so sections/pi-product.liquid stays untouched.
 *
 * It refreshes the drawer through Horizon's own section renderer rather than
 * reimplementing a cart refresh, and it degrades in one direction only:
 *
 *   before the item is added   fall back to the native submit
 *   after the item is added    go to /cart
 *
 * That split matters. Falling back to form.submit() after a successful add would
 * post the same line a second time, and on unique work that is the worst bug
 * this file could have.
 */

import { fetchConfig } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';

const FORM = 'form.pi-pdp__form';
const DRAWER_SECTION = 'cart-drawer-section';
const DRAWER = 'theme-drawer#cart-drawer';
const CART_COUNT = '[data-pi-cart-count]';

/** Minimum time the progress line stays up, so it reads as a state and not a flicker. */
const MIN_PROGRESS_MS = 450;

/**
 * Waits out the remainder of the minimum progress time.
 * @param {number} startedAt performance.now() from when the request began
 * @returns {Promise<void>}
 */
function settle(startedAt) {
  const left = MIN_PROGRESS_MS - (performance.now() - startedAt);
  return left > 0 ? new Promise((resolve) => setTimeout(resolve, left)) : Promise.resolve();
}

/**
 * Writes the new count into the header's badge. Only the number: the word next
 * to it is translated by sections/pi-header.liquid and stays as it is.
 * @param {number} count the new item count
 */
function updateHeaderCount(count) {
  for (const badge of document.querySelectorAll(CART_COUNT)) {
    badge.textContent = String(count);
  }
}

/**
 * Opens the drawer. The module is deferred, so on a fast click the custom
 * element may not have upgraded yet.
 * @returns {Promise<void>}
 */
async function openDrawer() {
  await customElements.whenDefined('theme-drawer');
  // Re-queried after the morph: morph keeps the <theme-drawer> node itself
  // (data-skip-node-update) but this stays correct if that ever changes.
  const drawer = /** @type {any} */ (document.querySelector(DRAWER));
  if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
}

/**
 * Handles one add-to-cart submit.
 * @param {SubmitEvent} event
 * @param {HTMLFormElement} form
 */
async function onSubmit(event, form) {
  // No drawer on this page — cart_type is 'page', or we are already on /cart.
  // Leave the native submit alone; it does the right thing.
  if (!document.querySelector(DRAWER)) return;

  event.preventDefault();

  const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[type="submit"]'));
  const startedAt = performance.now();
  let added = false;

  /** Puts the button back the way the visitor found it. */
  const release = () => {
    if (!button) return;
    button.removeAttribute('data-pi-adding');
    button.disabled = false;
  };

  if (button) {
    button.setAttribute('data-pi-adding', '');
    button.disabled = true;
  }

  try {
    // Matches product-form.js:455-462 — Accept: text/html against cart_add_url,
    // whose reply is still JSON.
    const config = fetchConfig('javascript', { body: new FormData(form) });
    const response = await fetch(Theme.routes.cart_add_url, {
      ...config,
      headers: { ...config.headers, Accept: 'text/html' },
    });
    const result = await response.json();

    // Shopify answers a refused line with a `status` field and an HTTP 4xx.
    if (!response.ok || result.status) {
      console.warn('[pi-cart] add to cart refused:', result.description || result.message);
      release();
      form.submit();
      return;
    }

    added = true;

    // The add reply carries the new line, not the cart, so ask for the count
    // rather than deriving it.
    const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
    updateHeaderCount(cart.item_count);

    // 'full' and not 'hydration': on the first add the drawer goes from its
    // empty markup to a filled cart, which is a structural change.
    await sectionRenderer.renderSection(DRAWER_SECTION, { cache: false, mode: 'full' });
    await settle(startedAt);
    await openDrawer();
    release();
  } catch (error) {
    console.warn('[pi-cart] add to cart fell back:', error);
    release();
    // Never re-post after a successful add.
    if (added) window.location.assign(Theme.routes.cart_url);
    else form.submit();
  }
}

// The darkened layer under the drawer (cart-drawer.liquid): a click on it closes
// the drawer, as a click on the backdrop does below 990 px.
document.addEventListener('click', (event) => {
  const target = /** @type {Element | null} */ (event.target);
  if (!target?.closest?.('[data-pi-cart-scrim]')) return;
  const drawer = /** @type {any} */ (document.querySelector(DRAWER));
  if (drawer?.isOpen && typeof drawer.close === 'function') drawer.close();
});

// Delegated, so it survives a product section re-render in the theme editor.
document.addEventListener('submit', (event) => {
  const target = /** @type {Element | null} */ (event.target);
  const form = target?.closest?.(FORM);
  if (form instanceof HTMLFormElement) onSubmit(/** @type {SubmitEvent} */ (event), form);
});
